/**
 * Deepfake replies: TanStack hooks over `/v1/deepfake/replies` and `/v1/deepfake/threads/:id`.
 *
 * HumanFirewall's Deepfake Simulation is the authorised, consented awareness exercise an organisation
 * runs against its OWN employees. When an employee answers the simulation e-mail the admin reads the
 * reply in the product. These hooks list the cross-campaign inbox, count what waits for an answer (the
 * sidebar badge), read one thread, read the policy the screen needs, and keep the screen fresh.
 *
 * NEVER A REPLY BODY. Every response is parsed against the mirror of the API's schema
 * (`lib/deepfake/replies-schemas.ts`), which names the redacted preview and the masked kinds and nothing
 * else; a field that leaked onto the wire (a body, raw HTML, MIME) is stripped here, before any component
 * can render it, and a response that drifted from the contract is an error the screen can show.
 *
 * Keys come from `lib/deepfake/query-keys.ts` (no mode, no workspace: tenant isolation is
 * WorkspaceCacheGuard plus the `x-workspace-id` header `fetchApi` sends).
 *
 * LIVE. The API has one stream per campaign (`GET /v1/deepfake/campaigns/:id/stream`), a signal that
 * something changed and never a copy of the data. `useDeepfakeRepliesLive` watches the campaign the open
 * thread belongs to and re-reads the replies queries once, after a short wait, when it speaks; every
 * screen watching the same campaign shares ONE connection. The inbox and the counts also refresh on a
 * timer while the tab is visible, which covers the campaigns nobody is watching and a stream that is
 * down (the API refuses a stream when one user holds too many).
 */
import { useEffect } from 'react'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiClientError, buildQueryString, fetchApi } from '@/lib/api/client'
import { openEventStream } from '@/lib/api/event-stream'
import { useWorkspaceStore } from '@/stores/workspace-store'
import { z } from 'zod'
import { deepfakeKeys } from '@/lib/deepfake/query-keys'
import {
  DeepfakeReplyCountsSchema,
  DeepfakeReplyListResponseSchema,
  DeepfakeReplyPolicySchema,
  DeepfakeThreadDetailSchema,
  type DeepfakeReplyCounts,
  type DeepfakeReplyListResponse,
  type DeepfakeReplyPolicy,
  type DeepfakeRepliesListParams,
  type DeepfakeThreadDetail,
} from '@/lib/deepfake/replies-schemas'
import { chronological } from '@/lib/deepfake/replies-view'

const REPLIES = '/deepfake/replies'
const THREADS = '/deepfake/threads'

/** How often the inbox and the counts re-read by themselves while the tab is visible. */
export const REPLIES_POLL_MS = 30_000
/** The sidebar badge is less urgent than the screen. */
export const REPLY_COUNTS_POLL_MS = 60_000
/**
 * How long a burst of live signals waits before the screen re-reads, counted from the FIRST of them (a campaign
 * with many replies at once costs one re-read, and a busy one is not starved by a wait that keeps moving).
 */
export const REPLIES_REFRESH_DEBOUNCE_MS = 1500
/** The server pings every 25 s, so a silent connection is a dead one. */
const STREAM_HEARTBEAT_TIMEOUT_MS = 70_000
/** The server refuses a stream when this user already holds the most one user may hold. */
const TOO_MANY_STREAMS = 429

// ── the plain calls ──────────────────────────────────────────────────────────

export const deepfakeRepliesApi = {
  async list(params: DeepfakeRepliesListParams): Promise<DeepfakeReplyListResponse> {
    return DeepfakeReplyListResponseSchema.parse(await fetchApi<unknown>(`${REPLIES}${buildQueryString({ ...params })}`))
  },
  async counts(): Promise<DeepfakeReplyCounts> {
    return DeepfakeReplyCountsSchema.parse(await fetchApi<unknown>(`${REPLIES}/counts`))
  },
  /** One thread, with messages and timeline in the order they happened whatever order they arrived in. */
  async thread(id: string): Promise<DeepfakeThreadDetail> {
    const detail = DeepfakeThreadDetailSchema.parse(await fetchApi<unknown>(`${THREADS}/${id}`))
    return {
      ...detail,
      messages: chronological(detail.messages, (m) => m.createdAt),
      timeline: chronological(detail.timeline, (e) => e.at),
    }
  },
  async policy(): Promise<DeepfakeReplyPolicy> {
    return DeepfakeReplyPolicySchema.parse(await fetchApi<unknown>('/deepfake/settings'))
  },
}

// ── queries ──────────────────────────────────────────────────────────────────

/** The inbox, one server-side page at a time (the filter and the page both run on the server). */
export function useDeepfakeReplies(params: DeepfakeRepliesListParams, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: deepfakeKeys.replies.list(params),
    queryFn: () => deepfakeRepliesApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: false,
  })
}

/** What waits for an answer, aggregated in SQL (the browser never counts rows): the sidebar badge and the chips. */
export function useDeepfakeReplyCounts(options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [...deepfakeKeys.replies.all(), 'counts'] as const,
    queryFn: () => deepfakeRepliesApi.counts(),
    staleTime: 10_000,
    refetchInterval: options.refetchInterval ?? REPLY_COUNTS_POLL_MS,
    refetchIntervalInBackground: false,
  })
}

export function useDeepfakeThread(id: string | null | undefined) {
  return useQuery({
    queryKey: deepfakeKeys.replies.thread(id ?? ''),
    queryFn: () => deepfakeRepliesApi.thread(id as string),
    enabled: !!id,
    // A 404 is an answer (the thread is gone, or it is another tenant's), not a reason to ask three more times.
    retry: (count, error) => !(error instanceof ApiClientError && error.statusCode === 404) && count < 2,
  })
}

/**
 * Whether the agent may answer by itself, and how many answers a thread may get: a few fields of the effective
 * policy. Its own key under the settings branch: the settings screen caches the whole policy under
 * `settings.effective()`, and one key must not hold two shapes.
 */
export function useDeepfakeReplyPolicy() {
  return useQuery({
    queryKey: [...deepfakeKeys.settings.effective(), 'reply-policy'] as const,
    queryFn: () => deepfakeRepliesApi.policy(),
    staleTime: 60_000,
  })
}

// ── the campaign a link names ────────────────────────────────────────────────

const CampaignNameSchema = z.object({ name: z.string() })

/**
 * The name of the campaign a link points the inbox at (`?campaignId=`), for the "Campaign: <name>" chip. A campaign
 * with no conversations yet has no row to take the name from, so it is read from the campaign itself. Only the name is
 * kept, under its own key (the campaign screens cache the whole campaign under `campaigns.detail`, and one key must not
 * hold two shapes); no answer is not a name, and the chip then says "one campaign" rather than invent one.
 */
export function useDeepfakeReplyCampaignName(campaignId: string | null | undefined) {
  return useQuery({
    queryKey: [...deepfakeKeys.campaigns.detail(campaignId ?? ''), 'reply-name'] as const,
    queryFn: async () => CampaignNameSchema.parse(await fetchApi<unknown>(`/deepfake/campaigns/${campaignId}`)).name,
    enabled: !!campaignId,
    staleTime: 5 * 60_000,
    retry: false,
  })
}

// ── live updates ─────────────────────────────────────────────────────────────

type Frame = 'change' | 'ready'

interface SharedStream {
  close: () => void
  listeners: Set<(frame: Frame) => void>
}

/** Open streams, by campaign and domain: every component watching a campaign shares one connection. */
const streams = new Map<string, SharedStream>()

function subscribeToCampaign(campaignId: string, listener: (frame: Frame) => void): () => void {
  const domainId = useWorkspaceStore.getState().activeDomainId
  const key = `${campaignId}|${domainId ?? ''}`
  let stream = streams.get(key)
  if (!stream) {
    const created: SharedStream = { close: () => {}, listeners: new Set() }
    streams.set(key, created)
    created.close = openEventStream(`/deepfake/campaigns/${campaignId}/stream`, {
      // The stream is checked in the same domain as the reads it stands in for.
      headers: domainId && domainId.trim() ? { 'x-domain-id': domainId } : {},
      heartbeatTimeoutMs: STREAM_HEARTBEAT_TIMEOUT_MS,
      // Too many streams open: stop, do not climb the reconnect ladder (each attempt counts against the same limit).
      // The inbox's own timer keeps the screen fresh.
      onRefused: (status) => (status === TOO_MANY_STREAMS ? 'stop' : undefined),
      onEvent: (name) => {
        // `ping`, an `error` frame (a code for the server's log) and any frame this build does not know are ignored.
        if (name !== 'change' && name !== 'ready') return
        for (const each of [...created.listeners]) {
          try {
            each(name)
          } catch {
            // One screen failing must not stop the others hearing the change.
          }
        }
      },
    })
    stream = created
  }
  const joined = stream
  joined.listeners.add(listener)
  return () => {
    joined.listeners.delete(listener)
    if (joined.listeners.size > 0 || streams.get(key) !== joined) return
    streams.delete(key)
    joined.close()
  }
}

/**
 * Watches one campaign and, when its stream says something changed (or that it is subscribed, because anything may
 * have changed while nothing listened), re-reads the replies queries once, after `REPLIES_REFRESH_DEBOUNCE_MS`.
 * Nothing is copied from an event into the cache: the API stays the only source of what a thread looks like.
 * Opens nothing without a campaign; closes with the last screen that watches it.
 */
export function useDeepfakeRepliesLive(campaignId: string | null | undefined): void {
  const client = useQueryClient()
  useEffect(() => {
    if (!campaignId) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const reread = () => {
      if (timer !== null) return
      timer = setTimeout(() => {
        timer = null
        void client.invalidateQueries({ queryKey: deepfakeKeys.replies.all() })
      }, REPLIES_REFRESH_DEBOUNCE_MS)
    }
    const unsubscribe = subscribeToCampaign(campaignId, reread)
    return () => {
      unsubscribe()
      if (timer !== null) clearTimeout(timer)
      timer = null
    }
  }, [campaignId, client])
}
